import type { AttritionPoint } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useId, useState } from 'react';
import { useAuth } from '../api.js';
import { formatCurrency } from '../lib/status.js';
import { Card } from './ui.js';

const MONTHS = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Commission flagged at risk, month over month — dependency-free SVG area chart. */
export function CommissionAtRiskChart({
  carrierId,
  workspaceId,
}: { carrierId?: string; workspaceId?: string }) {
  const { client } = useAuth();
  const [points, setPoints] = useState<AttritionPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    setPoints(null);
    client
      .attritionSeries({ months: 12, carrierId, workspaceId })
      .then((r) => active && setPoints(r.data))
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      active = false;
    };
  }, [client, carrierId, workspaceId]);

  return (
    <Card>
      <div className="mb-1 flex items-baseline justify-between">
        <h2 className="font-semibold">Commission at risk — month over month</h2>
        <Trend points={points} />
      </div>
      <p className="mb-4 text-xs text-neutral-500">
        Commission that dropped or was reduced vs the prior period — flagged for review.
      </p>
      {error ? (
        <p className="py-8 text-center text-sm text-rose-600">{error}</p>
      ) : !points ? (
        <div className="h-44 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-800/60" />
      ) : points.length < 2 ? (
        <p className="py-8 text-center text-sm text-neutral-500">
          Not enough history yet — load at least two periods to see a trend.
        </p>
      ) : (
        <Chart points={points} hover={hover} setHover={setHover} />
      )}
    </Card>
  );
}

function Trend({ points }: { points: AttritionPoint[] | null }) {
  if (!points || points.length === 0) return null;
  const last = points[points.length - 1];
  const prev = points.length > 1 ? points[points.length - 2] : undefined;
  if (!last) return null;
  const cur = last.commissionAtRisk;
  const delta = prev ? cur - prev.commissionAtRisk : 0;
  const up = delta > 0.005;
  const down = delta < -0.005;
  // More at-risk dollars is worse → red on the way up, green on the way down.
  const color = up ? 'text-rose-600' : down ? 'text-emerald-600' : 'text-neutral-500';
  return (
    <span className="flex items-baseline gap-2 text-sm">
      <span className="text-2xl font-semibold text-neutral-900 dark:text-white">
        {formatCurrency(cur)}
      </span>
      {prev && (
        <span className={color}>
          {up ? '▲' : down ? '▼' : '—'} {formatCurrency(Math.abs(delta))}
        </span>
      )}
    </span>
  );
}

function Chart({
  points,
  hover,
  setHover,
}: {
  points: AttritionPoint[];
  hover: number | null;
  setHover: (i: number | null) => void;
}) {
  const gradId = useId();
  const W = 720;
  const H = 200;
  const padX = 8;
  const padTop = 16;
  const padBottom = 28;
  const max = Math.max(1, ...points.map((p) => p.commissionAtRisk)) * 1.15;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const x = (i: number) =>
    padX + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v: number) => padTop + innerH - (v / max) * innerH;

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.commissionAtRisk)}`)
    .join(' ');
  const area = `${line} L${x(points.length - 1)},${padTop + innerH} L${x(0)},${padTop + innerH} Z`;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label="Commission at risk trend"
      >
        <title>Month-over-month commission at risk</title>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fb7185" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#fb7185" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={padX}
            x2={W - padX}
            y1={padTop + innerH * (1 - f)}
            y2={padTop + innerH * (1 - f)}
            className="stroke-neutral-200 dark:stroke-neutral-800"
            strokeWidth="1"
          />
        ))}

        <path d={area} fill={`url(#${gradId})`} />
        <path d={line} fill="none" stroke="#f43f5e" strokeWidth="2.5" strokeLinejoin="round" />

        {points.map((p, i) => (
          <g key={p.period}>
            <circle
              cx={x(i)}
              cy={y(p.commissionAtRisk)}
              r={hover === i ? 5 : 3.5}
              fill="#f43f5e"
              stroke="white"
              strokeWidth="1.5"
            />
            <text x={x(i)} y={H - 8} textAnchor="middle" className="fill-neutral-400 text-[10px]">
              {label(p)}
            </text>
            <rect
              x={x(i) - innerW / points.length / 2}
              y={0}
              width={innerW / points.length}
              height={H}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          </g>
        ))}
      </svg>

      {hover !== null && points[hover] && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg bg-neutral-900 px-2.5 py-1.5 text-xs text-white shadow-lg"
          style={{
            left: `${(x(hover) / W) * 100}%`,
            top: `${(y(points[hover].commissionAtRisk) / H) * 100}%`,
          }}
        >
          <div className="font-medium">
            {formatCurrency(points[hover].commissionAtRisk)} at risk
          </div>
          <div className="text-neutral-300">
            {formatPeriodLong({ year: points[hover].year, month: points[hover].month })}
          </div>
        </div>
      )}
    </div>
  );
}

function label(p: AttritionPoint): string {
  return `${MONTHS[p.month] ?? p.month} ’${String(p.year).slice(2)}`;
}
